/**
 * ChartCard - A single chart widget in the patient dashboard
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { VegaEmbed } from "react-vega";
import { API_BASE } from "../config";
import { fetchWithRetry } from "../lib/fetchRetry";
import { useAuth } from "../auth/AuthProvider";
import ConfirmDialog from "./ConfirmDialog";
import {
  buildChartSpec, buildMessageSpec,
  buildCorrelationSpec, computeCorrelations,
  buildTopicTrendSpec, computeTopicTrend,
  buildCooccurrenceSpec, computeCooccurrence,
  buildInstabilitySpec, computeInstability,
  buildAssessmentSpec, computeAssessmentSeries,
  buildLeadLagSpec, computeLeadLag,
  buildDynamicsSpec,
  buildEarlyWarningSpec, computeEarlyWarning,
  buildNetworkSpec, computeNetwork,
  buildPhysioSpec, computePhysio,
  MIN_SESSIONS,
} from "../lib/chartSpecs";
import type { ExtractorRow, AnalysisReason } from "../lib/chartSpecs";
import { stripClientName } from "../lib/chartTitle";
import type {
  ChartConfig, ChartType, ChartHint, OuraDailyRow, SessionMetricsRow,
} from "../types/charts";

/** Inject keyframes once for the spinning border animation */
const STYLE_ID = "chart-card-spin";
if (typeof document !== "undefined" && !document.getElementById(STYLE_ID)) {
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes chart-border-spin {
      from { transform: rotate(0deg); }
      to   { transform: rotate(360deg); }
    }
    .chart-extracting {
      position: relative;
      padding: 3px;
      border-radius: 19px;
      overflow: hidden;
    }
    .chart-extracting > * {
      position: relative;
      z-index: 1;
    }
    .chart-extracting::before {
      content: "";
      position: absolute;
      width: 150%;
      height: 150%;
      top: -25%;
      left: -25%;
      background: conic-gradient(
        transparent 200deg,
        var(--color-success) 260deg,
        var(--color-success-ghost) 310deg,
        transparent 360deg
      );
      animation: chart-border-spin 3s linear infinite;
      z-index: 0;
    }
  `;
  document.head.appendChild(style);
}

/** Minimal hourglass icon to signal "running" */
const HourglassIcon: React.FC<React.SVGProps<SVGSVGElement>> = (p) => (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...p}
  >
    <path d="M6 2h12M6 22h12" />
    <path d="M6 2c0 5 5 6 6 10 1-4 6-5 6-10" />
    <path d="M6 22c0-5 5-6 6-10 1 4 6 5 6 10" />
  </svg>
);

interface ServerPrompt {
  id: string;
  title: string;
  kind: string;
}

type Props = {
  chart: ChartConfig;
  prompts: ServerPrompt[];
  patientName?: string;
  onUpdate: (chartId: string, updates: Partial<ChartConfig>) => void;
  onDelete: (chartId: string) => void;
};

const CHART_TYPES: { value: ChartType; label: string }[] = [
  { value: "stacked_bar", label: "Timeline" },
  { value: "correlation", label: "Correlation" },
  { value: "topic_shift", label: "Trend" },
  { value: "cooccurrence", label: "Co-occurrence" },
  { value: "volatility", label: "Instability" },
  { value: "assessment", label: "Assessments" },
  { value: "leadlag", label: "Lead-Lag (Oura)" },
  { value: "dynamics", label: "Session Dynamics" },
  { value: "earlywarning", label: "Early Warning" },
  { value: "network", label: "Theme Network" },
  { value: "physio", label: "Body trajectory" },
];

const CHART_HINT_KEYS: Record<ChartType, string> = {
  stacked_bar: "charts.hintBar",
  correlation: "charts.hintCorrelation",
  topic_shift: "charts.hintTopicShift",
  cooccurrence: "charts.hintCooccurrence",
  volatility: "charts.hintVolatility",
  assessment: "charts.hintAssessment",
  leadlag: "charts.hintLeadlag",
  dynamics: "charts.hintDynamics",
  earlywarning: "charts.hintEarlywarning",
  network: "charts.hintNetwork",
  physio: "charts.hintPhysio",
};

const CHART_DESC_KEYS: Record<ChartType, string> = {
  stacked_bar: "charts.descBar",
  correlation: "charts.descCorrelation",
  topic_shift: "charts.descTopicShift",
  cooccurrence: "charts.descCooccurrence",
  volatility: "charts.descVolatility",
  assessment: "charts.descAssessment",
  leadlag: "charts.descLeadlag",
  dynamics: "charts.descDynamics",
  earlywarning: "charts.descEarlywarning",
  network: "charts.descNetwork",
  physio: "charts.descPhysio",
};

function useSize(ref: React.RefObject<HTMLElement | null>) {
  const [size, setSize] = useState({ width: 0, height: 0 });
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        const w = Math.round(e.contentRect.width);
        const h = Math.round(e.contentRect.height);
        setSize((prev) => (prev.width === w && prev.height === h ? prev : { width: w, height: h }));
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return size;
}

const CHART_HEIGHT = 280;

const ChartCard: React.FC<Props> = ({ chart, prompts, patientName, onUpdate, onDelete }) => {
  const { t } = useTranslation();
  // Redundant own-name suffix removed for display; stored title keeps it.
  const displayTitle = stripClientName(chart.title, patientName);
  const { ensureToken } = useAuth();
  const cardRef = useRef<HTMLDivElement>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Track width for vega chart
  const { width: currentCardW } = useSize(cardRef);
  const vegaW = Math.max(300, Math.floor(currentCardW - 32));
  const vegaH = CHART_HEIGHT;

  const [rows, setRows] = useState<ExtractorRow[]>([]);
  const [ouraDaily, setOuraDaily] = useState<OuraDailyRow[]>([]);
  const [sessionMetrics, setSessionMetrics] = useState<SessionMetricsRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [extractResult, setExtractResult] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [lastN, setLastN] = useState<number>((chart.config?.last_n as number) || 0);

  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(chart.title);
  const [editDescription, setEditDescription] = useState(chart.description || "");
  const [editChartType, setEditChartType] = useState<ChartType>(chart.chart_type);
  const [editExtractorIds, setEditExtractorIds] = useState<string[]>(chart.extractor_ids);
  const [editLastN, setEditLastN] = useState<number>(lastN);
  const [editRender, setEditRender] = useState<"" | "rows" | "overlay">(
    (chart.config?.render as "" | "rows" | "overlay") || ""
  );

  // Fetch topic distribution data per extractor (plus auxiliary series for
  // dynamics / lead-lag chart types)
  const fetchData = useCallback(async () => {
    if (!chart.patient_id) return;
    setBusy(true);
    setError(null);

    try {
      const token = await ensureToken();

      // Shared helpers (also used by leadlag/dynamics/physio below) — kept
      // at function scope so every branch that needs session dates or daily
      // Oura physiology hits the same endpoints the same way.
      const pad = (d: string, days: number) => {
        const dt = new Date(d + "T00:00:00Z");
        dt.setUTCDate(dt.getUTCDate() + days);
        return dt.toISOString().slice(0, 10);
      };
      const fetchSessionMetrics = async (): Promise<SessionMetricsRow[]> => {
        const r = await fetchWithRetry(
          `${API_BASE}/tags/session_metrics?patient_id=${encodeURIComponent(chart.patient_id)}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
        );
        if (!r.ok) {
          const text = await r.text().catch(() => "");
          throw new Error(`HTTP ${r.status} ${r.statusText}${text ? ` — ${text}` : ""}`);
        }
        const res = await r.json();
        return Array.isArray(res?.items) ? res.items : [];
      };
      const fetchOuraDaily = async (start: string, end: string): Promise<OuraDailyRow[]> => {
        const r = await fetchWithRetry(
          `${API_BASE}/tags/oura_daily?patient_id=${encodeURIComponent(chart.patient_id)}&start=${start}&end=${end}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
        );
        if (!r.ok) {
          const text = await r.text().catch(() => "");
          throw new Error(`HTTP ${r.status} ${r.statusText}${text ? ` — ${text}` : ""}`);
        }
        const res = await r.json();
        return Array.isArray(res?.items) ? res.items : [];
      };

      // Session dynamics is computed server-side from embeddings — it
      // needs no extractors at all.
      if (chart.chart_type === "dynamics") {
        setSessionMetrics(await fetchSessionMetrics());
        setRows([]);
        return;
      }

      // Physio is a self-contained branch (not an extractor-driven chart):
      // it needs session dates (for the vertical rules) plus ~60 days of
      // daily Oura physiology. It must return before the extractor logic
      // below, whose `extractorIds.length === 0` early-return would
      // otherwise swallow it.
      if (chart.chart_type === "physio") {
        const sm = await fetchSessionMetrics();
        setSessionMetrics(sm);
        const dates = sm.map((r) => r.date).sort();
        if (dates.length > 0) {
          const start = pad(dates[0], -60);
          const end = new Date().toISOString().slice(0, 10);
          setOuraDaily(await fetchOuraDaily(start, end));
        } else {
          setOuraDaily([]);
        }
        setRows([]);
        return;
      }

      const extractorIds = chart.extractor_ids && chart.extractor_ids.length > 0
        ? chart.extractor_ids
        : [];

      if (extractorIds.length === 0) {
        setRows([]);
        return;
      }

      // Fetch each extractor separately to get individual chart_hints
      const allRows: ExtractorRow[] = [];
      const fetches: Promise<void>[] = [];

      for (const extractorId of extractorIds) {
        fetches.push(
          (async () => {
            const url = `${API_BASE}/tags/topic_distribution?patient_id=${encodeURIComponent(chart.patient_id)}&prompt_pks=${encodeURIComponent(extractorId)}`;
            const r = await fetchWithRetry(url, {
              method: "GET",
              headers: token ? { Authorization: `Bearer ${token}` } : undefined,
            });

            if (!r.ok) {
              const text = await r.text().catch(() => "");
              throw new Error(`HTTP ${r.status} ${r.statusText}${text ? ` — ${text}` : ""}`);
            }

            const res = await r.json();
            const items = Array.isArray(res?.items) ? res.items : [];
            const prompt = prompts.find((p) => p.id === extractorId);
            const extractorTitle = prompt?.title || extractorId;
            const VALID_HINTS: ChartHint[] = ["simple", "stacked", "multi", "airtime", "assessment", "markers"];
            const hint: ChartHint = VALID_HINTS.includes(res?.chart_hint)
              ? res.chart_hint
              : "stacked";

            for (const it of items) {
              const item = it as Record<string, unknown>;
              const date = String(item?.date ?? "");
              const sid = String(item?.session_id ?? "");
              const topic = String(item?.topic ?? "");
              const percent = Number(item?.percent);
              const evidence = Array.isArray(item?.evidence)
                ? (item.evidence as unknown[]).map((q) => String(q))
                : undefined;
              if (date && topic && Number.isFinite(percent)) {
                allRows.push({
                  date,
                  session_id: sid,
                  topic,
                  percent,
                  evidence,
                  evidenceText: evidence?.length ? evidence.join(" • ") : undefined,
                  max_score: Number.isFinite(Number(item?.max_score)) && item?.max_score != null
                    ? Number(item.max_score)
                    : undefined,
                  extractor: extractorTitle,
                  extractorHint: hint,
                });
              }
            }
          })()
        );
      }

      await Promise.all(fetches);

      setRows(allRows);

      // Lead-lag additionally needs the daily Oura series spanning the
      // session range (±3 days for the lag window).
      if (chart.chart_type === "leadlag") {
        const dates = allRows.map((r) => r.date).sort();
        if (dates.length > 0) {
          const start = pad(dates[0], -3);
          const end = pad(dates[dates.length - 1], 3);
          setOuraDaily(await fetchOuraDaily(start, end));
        } else {
          setOuraDaily([]);
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [chart.patient_id, chart.extractor_ids, chart.chart_type, prompts, ensureToken]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  // Distinct topics present in the data (for the topic filter chips)
  const availableTopics = useMemo(() => {
    const s = new Set<string>();
    for (const r of rows) s.add(r.topic);
    return Array.from(s).sort();
  }, [rows]);

  // Persisted hidden topics set from chart config (empty = show all)
  const hiddenTopics = useMemo(() => {
    const h = chart.config?.hidden_topics;
    if (Array.isArray(h)) return new Set<string>(h.map((x) => String(x)));
    return new Set<string>();
  }, [chart.config]);

  const toggleTopic = useCallback(
    (topic: string) => {
      const next = new Set(hiddenTopics);
      if (next.has(topic)) next.delete(topic);
      else next.add(topic);
      onUpdate(chart.id, {
        config: { ...chart.config, hidden_topics: Array.from(next) },
      });
    },
    [chart.id, chart.config, hiddenTopics, onUpdate]
  );

  // Filter rows to last N unique session dates + topic filter
  const filteredRows = useMemo(() => {
    let r = rows;
    if (hiddenTopics.size > 0) r = r.filter((x) => !hiddenTopics.has(x.topic));
    if (!lastN || lastN <= 0) return r;
    const uniqueDates = Array.from(new Set(r.map((x) => x.date))).sort().reverse().slice(0, lastN);
    const dateSet = new Set(uniqueDates);
    return r.filter((x) => dateSet.has(x.date));
  }, [rows, lastN, hiddenTopics]);

  const spec = useMemo(() => {
    const dims = { width: vegaW, height: vegaH };
    if (busy) return buildMessageSpec(t("charts.loading"), dims);
    if (error) return buildMessageSpec(t("charts.errorLoading"), dims);

    const emptyMessage = (reason: AnalysisReason, maxN: number, fallbackKey: string) => {
      if (reason === "not_enough_sessions") {
        return t("charts.notEnoughSessions", { need: MIN_SESSIONS, have: maxN });
      }
      if (reason === "compositional_only") {
        return t("charts.compositionalExcluded");
      }
      return t(fallbackKey);
    };

    if (chart.chart_type === "correlation") {
      const a = computeCorrelations(filteredRows);
      if (a.results.length === 0) {
        return buildMessageSpec(emptyMessage(a.reason, a.maxN, "charts.noCorrelationData"), dims);
      }
      return buildCorrelationSpec(a.results, dims, displayTitle);
    }
    if (chart.chart_type === "topic_shift") {
      const a = computeTopicTrend(filteredRows);
      if (a.results.length === 0) {
        return buildMessageSpec(emptyMessage(a.reason, a.maxN, "charts.noTopicShiftData"), dims);
      }
      return buildTopicTrendSpec(a.results, dims, displayTitle);
    }
    if (chart.chart_type === "cooccurrence") {
      const a = computeCooccurrence(filteredRows);
      if (a.results.length === 0) {
        return buildMessageSpec(emptyMessage(a.reason, a.maxN, "charts.noCooccurrenceData"), dims);
      }
      return buildCooccurrenceSpec(a.results, dims, displayTitle);
    }
    if (chart.chart_type === "volatility") {
      const a = computeInstability(filteredRows);
      if (a.results.length === 0) {
        return buildMessageSpec(emptyMessage(a.reason, a.maxN, "charts.noVolatilityData"), dims);
      }
      return buildInstabilitySpec(a.results, dims, displayTitle);
    }
    if (chart.chart_type === "assessment") {
      const a = computeAssessmentSeries(filteredRows);
      if (a.results.length === 0) {
        return buildMessageSpec(emptyMessage(a.reason, a.maxN, "charts.noAssessmentData"), dims);
      }
      return buildAssessmentSpec(a.results, dims, displayTitle);
    }
    if (chart.chart_type === "physio") {
      const a = computePhysio(ouraDaily, sessionMetrics.map((r) => r.date));
      if (a.results.length === 0) return buildMessageSpec(t("charts.noOuraData"), dims);
      return buildPhysioSpec(a.results, sessionMetrics.map((r) => r.date), dims);
    }
    if (chart.chart_type === "leadlag") {
      const a = computeLeadLag(filteredRows, ouraDaily);
      if (a.results.length === 0) {
        if (a.reason === "missing_inputs") {
          return buildMessageSpec(t("charts.noOuraData"), dims);
        }
        return buildMessageSpec(emptyMessage(a.reason, a.maxN, "charts.noLeadlagData"), dims);
      }
      return buildLeadLagSpec(a.results, dims, displayTitle);
    }
    if (chart.chart_type === "dynamics") {
      return buildDynamicsSpec(sessionMetrics, dims, displayTitle);
    }
    if (chart.chart_type === "earlywarning") {
      const a = computeEarlyWarning(filteredRows);
      if (a.results.length === 0) {
        return buildMessageSpec(emptyMessage(a.reason, a.maxN, "charts.noEarlywarningData"), dims);
      }
      return buildEarlyWarningSpec(a.results, dims, displayTitle);
    }
    if (chart.chart_type === "network") {
      const net = computeNetwork(filteredRows);
      if (net.edges.length === 0) {
        if (net.reason === "ok" && net.tested > 0) {
          return buildMessageSpec(t("charts.networkNoEdges", { tested: net.tested }), dims);
        }
        return buildMessageSpec(emptyMessage(net.reason, net.maxN, "charts.noNetworkData"), dims);
      }
      return buildNetworkSpec(net, dims, displayTitle);
    }

    const renderOverride = (((r) => (r === "rows" || r === "overlay" ? r : undefined))(chart.config?.render)) || undefined;
    return buildChartSpec(chart.chart_type, filteredRows, dims, displayTitle, renderOverride);
  }, [chart.chart_type, chart.config, displayTitle, filteredRows, ouraDaily, sessionMetrics, busy, error, vegaW, vegaH, t]);

  const handleSaveEdit = () => {
    setLastN(editLastN);
    onUpdate(chart.id, {
      title: editTitle,
      description: editDescription.trim(),
      chart_type: editChartType,
      extractor_ids: editExtractorIds,
      config: { ...chart.config, last_n: editLastN || undefined, render: editRender || undefined },
    });
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditTitle(chart.title);
    setEditDescription(chart.description || "");
    setEditChartType(chart.chart_type);
    setEditExtractorIds(chart.extractor_ids);
    setEditLastN(lastN);
    setEditRender((chart.config?.render as "" | "rows" | "overlay") || "");
    setIsEditing(false);
  };

  // Generate title from selected extractor names
  const generateTitleFromExtractors = useCallback((extractorIds: string[]) => {
    if (extractorIds.length === 0) return "";
    const names = extractorIds
      .map((id) => prompts.find((p) => p.id === id)?.title || id)
      .filter(Boolean);
    if (names.length === 0) return "";
    if (names.length === 1) return names[0];
    if (names.length === 2) return `${names[0]} and ${names[1]}`;
    // 3+ items: "A, B, and C"
    return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
  }, [prompts]);

  const handleExtractorToggle = (id: string) => {
    setEditExtractorIds((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      // Auto-generate title from selected extractors
      setEditTitle(generateTitleFromExtractors(next));
      return next;
    });
  };

  const handleRunExtractor = useCallback(async () => {
    setExtracting(true);
    setExtractResult(null);
    try {
      const token = await ensureToken();
      const res = await fetchWithRetry(`${API_BASE}/tags/extract`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          prompt_pks: chart.extractor_ids,
          patient_id: chart.patient_id,
        }),
      });
      if (!res.ok && res.status !== 202) {
        const data = await res.json();
        throw new Error(data.error || `HTTP ${res.status}`);
      }

      // Poll status every 3s. A single network blip is tolerated (and
      // logged); consecutive failures abort the poll and surface the error
      // instead of spinning forever.
      if (pollRef.current) clearInterval(pollRef.current);
      let polls = 0;
      let consecutiveFailures = 0;
      pollRef.current = setInterval(async () => {
        polls++;
        try {
          const t = await ensureToken();
          const sr = await fetchWithRetry(
            `${API_BASE}/tags/extract?patient_id=${encodeURIComponent(chart.patient_id)}`,
            { headers: t ? { Authorization: `Bearer ${t}` } : undefined }
          );
          if (!sr.ok) throw new Error(`HTTP ${sr.status} ${sr.statusText}`);
          const st = await sr.json();
          consecutiveFailures = 0;
          if (st.status === "done" || st.status === "error" || polls >= 40) {
            clearInterval(pollRef.current!);
            pollRef.current = null;
            setExtracting(false);
            setExtractResult(st.status === "done" ? "Done" : "Error");
            if (st.status === "error") {
              setError(String(st.error || "extraction failed"));
            }
            fetchData();
          }
        } catch (pollErr) {
          consecutiveFailures++;
          console.error("[ChartCard] extract status poll failed:", pollErr);
          if (consecutiveFailures >= 3) {
            clearInterval(pollRef.current!);
            pollRef.current = null;
            setExtracting(false);
            setExtractResult("Error");
            setError(pollErr instanceof Error ? pollErr.message : String(pollErr));
          }
        }
      }, 3000);
    } catch (e) {
      console.error("[ChartCard] extract start failed:", e);
      setError(e instanceof Error ? e.message : String(e));
      setExtractResult("Error");
      setExtracting(false);
    }
  }, [ensureToken, chart.extractor_ids, chart.patient_id, fetchData]);

  const cardStyle: React.CSSProperties = {
    background: "transparent",
    borderTop: "1px solid var(--color-title)",
    width: "100%",
  };

  const headerStyle: React.CSSProperties = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: "13px 0 6px",
  };

  const titleStyle: React.CSSProperties = {
    fontFamily: "var(--font-display)",
    fontWeight: 600,
    fontSize: 17,
    color: "var(--color-title)",
  };

  const descriptionStyle: React.CSSProperties = {
    fontSize: 12.5,
    color: "var(--color-muted)",
    marginTop: 4,
    lineHeight: 1.5,
  };

  const btnStyle: React.CSSProperties = {
    padding: "4px 12px",
    border: "1px solid var(--color-border)",
    borderRadius: 999,
    background: "var(--color-surface)",
    color: "var(--color-ink)",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  };

  return (
    <div className={extracting ? "chart-extracting" : undefined} style={{ borderRadius: 4 }}>
    <div ref={cardRef} style={cardStyle}>
      <div style={headerStyle}>
        {isEditing ? (
          <div style={{ flex: 1, marginRight: 8 }}>
            <input
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              style={{
                ...titleStyle,
                border: "1px solid var(--color-border)",
                borderRadius: 6,
                padding: "4px 8px",
                width: "100%",
              }}
            />
            <textarea
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              placeholder={t("charts.descriptionPlaceholder")}
              rows={2}
              style={{
                width: "100%",
                marginTop: 6,
                border: "1px solid var(--color-border)",
                borderRadius: 6,
                padding: "4px 8px",
                fontSize: 12,
                fontFamily: "inherit",
                color: "var(--color-ink)",
                resize: "vertical",
              }}
            />
          </div>
        ) : (
          <div style={{ flex: 1, marginRight: 8 }}>
            <div style={titleStyle}>{displayTitle}</div>
            {chart.description && (
              <div style={descriptionStyle}>{chart.description}</div>
            )}
          </div>
        )}

        <div style={{ display: "flex", gap: 6 }}>
          {isEditing ? (
            <>
              <button style={{ ...btnStyle, background: "var(--color-brand)", color: "#fff", borderColor: "var(--color-brand)" }} onClick={handleSaveEdit}>
                {t("common.save")}
              </button>
              <button style={btnStyle} onClick={handleCancelEdit}>
                {t("common.cancel")}
              </button>
            </>
          ) : (
            <>
              <button
                style={{ ...btnStyle, background: extracting ? "var(--color-brand-ghost)" : "var(--color-surface)" }}
                onClick={handleRunExtractor}
                disabled={extracting || busy}
                title={t("charts.runExtractor")}
              >
                {extracting ? <HourglassIcon width={14} height={14} /> : busy ? "..." : extractResult || t("charts.extract")}
              </button>
              <button style={btnStyle} onClick={() => setIsEditing(true)}>
                {t("common.edit")}
              </button>
              <button
                style={{ ...btnStyle, color: "var(--color-danger)", borderColor: "var(--color-danger-ghost)" }}
                onClick={() => setShowDeleteConfirm(true)}
              >
                {t("common.delete")}
              </button>
            </>
          )}
        </div>
      </div>

      {isEditing && (
        <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--color-border)", background: "var(--color-subsurface)" }}>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 12, alignItems: "flex-end" }}>
            <div>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                {t("charts.chartType")}
              </label>
              <select
                value={editChartType}
                onChange={(e) => setEditChartType(e.target.value as ChartType)}
                style={{
                  padding: "6px 10px",
                  border: "1px solid var(--color-border)",
                  borderRadius: 8,
                  fontSize: 13,
                }}
              >
                {CHART_TYPES.map((ct) => (
                  <option key={ct.value} value={ct.value}>
                    {ct.label}
                  </option>
                ))}
              </select>
              <div style={{ fontSize: 11, color: "var(--color-muted)", marginTop: 4, fontWeight: 600 }}>
                {t(CHART_HINT_KEYS[editChartType])}
              </div>
              <div style={{
                fontSize: 13,
                color: "var(--color-ink)",
                marginTop: 6,
                lineHeight: 1.6,
                background: "var(--color-subsurface)",
                border: "1px solid var(--color-border)",
                borderRadius: 8,
                padding: "10px 12px",
              }}>
                {t(CHART_DESC_KEYS[editChartType])}
              </div>
            </div>
            {editChartType === "stacked_bar" && (
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                  {t("charts.renderLabel")}
                </label>
                <select
                  value={editRender}
                  onChange={(e) => setEditRender(e.target.value as "" | "rows" | "overlay")}
                  style={{
                    padding: "6px 10px",
                    border: "1px solid var(--color-border)",
                    borderRadius: 8,
                    fontSize: 13,
                  }}
                >
                  <option value="">{t("charts.renderAuto")}</option>
                  <option value="rows">{t("charts.renderRows")}</option>
                  <option value="overlay">{t("charts.renderOverlay")}</option>
                </select>
              </div>
            )}
            <div>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                {t("charts.sessionsLabel")}
              </label>
              <select
                value={editLastN}
                onChange={(e) => setEditLastN(Number(e.target.value))}
                style={{
                  padding: "6px 10px",
                  border: "1px solid var(--color-border)",
                  borderRadius: 8,
                  fontSize: 13,
                }}
              >
                <option value={0}>{t("charts.sessionsAll")}</option>
                <option value={5}>Last 5</option>
                <option value={10}>Last 10</option>
                <option value={20}>Last 20</option>
                <option value={50}>Last 50</option>
              </select>
            </div>
          </div>

          <div>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
              {t("charts.extractors")}
            </label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {prompts.length === 0 && (
                <span style={{ color: "var(--color-muted)", fontSize: 12 }}>{t("charts.noExtractors")}</span>
              )}
              {prompts.map((p) => (
                <label
                  key={p.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    padding: "4px 8px",
                    border: "1px solid var(--color-border)",
                    borderRadius: 6,
                    background: editExtractorIds.includes(p.id) ? "var(--color-brand-ghost)" : "var(--color-surface)",
                    cursor: "pointer",
                    fontSize: 12,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={editExtractorIds.includes(p.id)}
                    onChange={() => handleExtractorToggle(p.id)}
                  />
                  {p.title || p.id}
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      {availableTopics.length > 1 && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 6,
            padding: "8px 16px",
            borderBottom: "1px solid var(--color-border)",
          }}
        >
          {availableTopics.map((tp) => {
            const active = !hiddenTopics.has(tp);
            return (
              <button
                key={tp}
                onClick={() => toggleTopic(tp)}
                style={{
                  padding: "3px 10px",
                  border: `1px solid ${active ? "var(--color-brand)" : "var(--color-border)"}`,
                  borderRadius: 999,
                  background: active ? "var(--color-brand-ghost)" : "var(--color-subsurface)",
                  color: active ? "var(--color-title)" : "var(--color-muted)",
                  fontSize: 11,
                  cursor: "pointer",
                  fontWeight: active ? 600 : 400,
                  textDecoration: active ? "none" : "line-through",
                }}
                title={active ? "Click to hide" : "Click to show"}
              >
                {tp}
              </button>
            );
          })}
        </div>
      )}

      <div style={{ padding: 12 }}>
        {error && (
          <div style={{ color: "var(--color-danger)", fontSize: 12, marginBottom: 8 }}>Error: {error}</div>
        )}
        <VegaEmbed
          spec={spec}
          options={{ actions: false, mode: "vega-lite", renderer: "svg" }}
          onError={(err) => console.error("[ChartCard] vega error:", err)}
        />
      </div>

      <ConfirmDialog
        open={showDeleteConfirm}
        title={t("common.delete")}
        message={t("charts.confirmDelete", { title: chart.title })}
        confirmLabel={t("common.delete")}
        variant="danger"
        onConfirm={() => {
          setShowDeleteConfirm(false);
          onDelete(chart.id);
        }}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </div>
    </div>
  );
};

export default ChartCard;
